"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { ControllerRenderProps, useForm } from "react-hook-form";

import { LoginRequest } from "@shared/proto/tenki/cloud/identity/v1beta1/identity_service_pb";
import { loginRequestSchema } from "@shared/proto/tenki/cloud/identity/v1beta1/identity_service_zod";
import { Banner } from "@shared/ui/banner";
import { Button } from "@shared/ui/button";
import { ButtonLabelLoader } from "@shared/ui/components/button-label-loader";
import { Input } from "@shared/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared/ui/old-form";
import { toast } from "@shared/ui/toast";
import { cn } from "@shared/ui/utils";

import GithubLogoIcon from "~/assets/svg/github";
import { Warning } from "~/assets/svg/warning";
import { api } from "~/trpc/client";
import { Flag } from "~/utils/flag/client";

interface LoginFormProps {
  flowId: string;
  flowError?: string;
  csrfToken?: string;
  loginMethod?: string;
  onSessionExpired: () => void;
}

function PasswordInput({
  field,
  hasError,
}: {
  field: ControllerRenderProps<LoginRequest, "password">;
  hasError: boolean;
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="group relative">
      <Input
        id="password"
        type={showPassword ? "text" : "password"}
        {...field}
        required
        className={hasError ? "border-input-controls-error" : ""}
      />{" "}
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className={cn(
          "text-static-primary absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer",
          !field.value.length && "text-static-secondary",
        )}
      >
        {showPassword ? (
          <EyeSlashIcon
            className="text-static-secondary group-focus-within:text-static-primary h-4 w-4"
            weight="fill"
          />
        ) : (
          <EyeIcon
            className={cn("text-static-secondary group-focus-within:text-static-primary h-4 w-4")}
            weight="fill"
          />
        )}
      </button>
    </div>
  );
}

export default function LoginForm({ flowId, flowError, csrfToken, loginMethod, onSessionExpired }: LoginFormProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(flowError ?? null);
  const [showBanner, setShowBanner] = useState(false);
  const ctx = api.useUtils();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get("return_to");
  const [flow, setFlow] = useState(loginMethod ?? "password");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loginProvider, setLoginProvider] = useState<string | null>(null);

  const defaultValues = {
    flowId,
    csrfToken,
    method: "password",
    code: "",
    email: "",
    password: "",
  };

  const loginForm = useForm<LoginRequest>({
    resolver: zodResolver(
      loginRequestSchema.extend({
        email: loginRequestSchema.shape.email.min(1, "Email is required").email("Must be a valid email"),
        password: loginRequestSchema.shape.password.min(1, "Password is required"),
      }),
    ),
    defaultValues,
  });

  const totpForm = useForm<LoginRequest>({
    resolver: zodResolver(
      loginRequestSchema.extend({
        code: loginRequestSchema.shape.code.unwrap().min(6, "Code must be 6 digits"),
      }),
    ),
    defaultValues: {
      ...defaultValues,
      method: "totp",
    },
    mode: "onChange",
  });

  const showErrMsg = (message: string) => {
    setErrorMessage(message);
    setShowBanner(true);
  };

  const login = api.identity.public.login.useMutation({
    onError: () => {
      setLoginProvider(null);
      const message =
        flow === "totp" ? "Failed to validate otp. Please try again" : "Failed to login. Please try again.";
      showErrMsg(message);
    },
    onSuccess: async (res: { status: string; message: string; returnTo: string }) => {
      setErrorMessage(null);
      setShowBanner(false);
      const returnToPath = returnTo ? decodeURIComponent(returnTo) : "/dashboard";

      if (res.status === "ok") {
        setIsRedirecting(true);
        window.location.href = returnToPath;
      } else if (res.status === "redirect") {
        setIsRedirecting(true);
        // GitHub OAuth redirect
        window.location.href = res.returnTo;
      } else {
        if (res.returnTo === "totp") {
          toast.success(res.message);
          setFlow(res.returnTo);
          await ctx.identity.public.getFlow.invalidate({ flowId, type: "login" });

          totpForm.setValue("email", loginForm.getValues("email"), {
            shouldDirty: true,
            shouldValidate: true,
          });

          totpForm.setValue("password", loginForm.getValues("password"), {
            shouldDirty: true,
            shouldValidate: true,
          });

          router.replace(`/auth/login?aal=true&return_to=${returnToPath}`);
        } else {
          if (res.message.toLowerCase().includes("invalid") && res.message.toLowerCase().includes("credential")) {
            showErrMsg(
              "Please verify your credentials and try again. If needed, use the Forgot Password link to reset your password.",
            );
          } else {
            showErrMsg(res.message);
          }
          if (res.message.toLowerCase().includes("session has expired")) {
            onSessionExpired();
          }
        }
      }
    },
    trpc: { context: { skipBatch: true } },
  });

  useEffect(() => {
    if (flow === "totp") {
      totpForm.setValue("flowId", flowId, {
        shouldDirty: true,
        shouldValidate: true,
      });
      totpForm.setValue("csrfToken", csrfToken!, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [flow, flowId, csrfToken, totpForm]);

  useEffect(() => {
    return () => {
      setIsRedirecting(false);
      setLoginProvider(null);
    };
  }, []);

  const onSubmit = (values: LoginRequest) => {
    if (!csrfToken) return;
    setLoginProvider("account");
    login.mutate({
      ...values,
      flowId,
      csrfToken,
    });
  };

  const onLoginWithGitHub = () => {
    if (!csrfToken) return;
    setLoginProvider("github");
    login.mutate({
      email: "",
      password: "",
      method: "oidc",
      provider: "github",
      flowId,
      csrfToken,
    });
  };

  if (flow === "totp") {
    return (
      <>
        <section className="mb-8">
          <h1 className="text-static-primary mb-2 text-2xl font-bold">Enter Your Code</h1>
          <p className="text-static-secondary">Enter the verification code from your authenticator app.</p>
        </section>
        <Form {...loginForm}>
          <form onSubmit={totpForm.handleSubmit(onSubmit)} className="grid gap-4" noValidate>
            <FormField
              control={totpForm.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-static-primary mb-2 block font-medium">6-digit Code</FormLabel>
                  <FormControl>
                    <Input
                      id="confirmationCode"
                      type="number"
                      minLength={6}
                      maxLength={6}
                      placeholder="123456"
                      className={`inline-block w-full ${totpForm.formState.errors.code ? "border-input-controls-error" : ""}`}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button type="submit" className="mt-6 w-full" disabled={!totpForm.formState.isValid || login.isPending}>
              <ButtonLabelLoader isPending={login.isPending} label="Continue" />
            </Button>
          </form>
        </Form>
      </>
    );
  }

  return (
    <Form {...loginForm}>
      {showBanner && errorMessage && (
        <Banner variant="warning" className="text-md mb-3 flex items-start space-x-2">
          <div className="">
            <Warning />
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium">Invalid email or password</span>
            <span className="pr-3 text-sm">{errorMessage}</span>
          </div>
        </Banner>
      )}
      <form onSubmit={loginForm.handleSubmit(onSubmit)} className="grid gap-4" noValidate>
        <section className="mb-4">
          <h1 className="text-static-primary text-2xl font-bold">Login</h1>
        </section>
        <section className="space-y-8">
          <div className="grid gap-2">
            <FormField
              control={loginForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <FormControl>
                    <Input
                      id="email"
                      type="email"
                      {...field}
                      autoFocus
                      className={loginForm.formState.errors.email ? "border-input-controls-error" : ""}
                    />
                  </FormControl>
                  {loginForm.formState.touchedFields.email && <FormMessage />}
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-2">
            <FormField
              control={loginForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center">
                    <FormLabel htmlFor="password">Password</FormLabel>
                  </div>
                  <FormControl>
                    <PasswordInput field={field} hasError={!!loginForm.formState.errors.password} />
                  </FormControl>
                  {loginForm.formState.touchedFields.password && <FormMessage />}
                </FormItem>
              )}
            />
            <Link
              href="/auth/recovery"
              prefetch={false}
              className="text-cta-link-rest hover:text-cta-link-hovered ml-auto inline-block text-sm underline decoration-solid underline-offset-[3px]"
            >
              Forgot Password
            </Link>
          </div>
        </section>

        <div className="mt-6 flex w-full flex-col gap-6">
          <Button
            type="submit"
            data-testid="login-submit-button"
            className="w-full"
            disabled={!loginForm.formState.isValid || login.isPending || isRedirecting}
          >
            <ButtonLabelLoader
              isPending={
                (login.isPending && loginProvider === "account") || (isRedirecting && loginProvider === "account")
              }
              label="Login"
            />
          </Button>

          <Flag flag="github_sso">
            <div className="flex items-center gap-2">
              <hr className="flex-1 border-neutral-600" />
              <span className="text-static-secondary text-center text-sm leading-5">Or</span>
              <hr className="flex-1 border-neutral-600" />
            </div>

            <Button
              variant="outline"
              type="button"
              className="border-cta-secondary-default-border bg-cta-secondary-rest w-full rounded-lg border"
              onClick={onLoginWithGitHub}
              disabled={login.isPending || isRedirecting}
            >
              <ButtonLabelLoader
                isPending={
                  (login.isPending && loginProvider === "github") || (isRedirecting && loginProvider === "github")
                }
                label={
                  <div className="flex items-center justify-center gap-2">
                    <GithubLogoIcon className="h-4 w-4" />
                    <span>Login With GitHub</span>
                  </div>
                }
              />
            </Button>
          </Flag>
        </div>
      </form>
    </Form>
  );
}
